import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './layouts/AppShell';
import { AuthLayout } from './layouts/AuthLayout';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { RequireRole } from '@/features/auth/RequireRole';
import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { PatientsListPage } from '@/features/patients/PatientsListPage';
import { PatientNewPage } from '@/features/patients/PatientNewPage';
import { PatientDetailPage } from '@/features/patients/PatientDetailPage';
import { PatientEditPage } from '@/features/patients/PatientEditPage';
import { PatientPrintPage } from '@/features/patients/PatientPrintPage';
import { ProceduresListPage } from '@/features/procedures/ProceduresListPage';
import { ProcedureNewPage } from '@/features/procedures/ProcedureNewPage';
import { ProcedureEditPage } from '@/features/procedures/ProcedureEditPage';
import { ProcedureDetailPage } from '@/features/procedures/ProcedureDetailPage';
import { ProcedurePrintPage } from '@/features/procedures/ProcedurePrintPage';
import { UsersListPage } from '@/features/users/UsersListPage';
import { RolesPage } from '@/features/roles/RolesPage';
import { CatalogPage } from '@/features/catalog/CatalogPage';
import { PayrollPage } from '@/features/payroll/PayrollPage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';
import { ConsentSignPage } from '@/features/consent/ConsentSignPage';
import { AnesthesiaSignPage } from '@/features/anesthesia/AnesthesiaSignPage';
import { ExpenseSignPage } from '@/features/expense/ExpenseSignPage';
import { ClinicalDocsListPage } from '@/features/clinical-docs/ClinicalDocsListPage';
import { ConsultaHistoricaPage } from '@/features/historial/ConsultaHistoricaPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { ConfiguracionPage } from '@/features/configuracion/ConfiguracionPage';
import { PathologyListPage } from '@/features/pathology/PathologyListPage';
import { PathologyNewPage } from '@/features/pathology/PathologyNewPage';
import { PortalLoginPage } from '@/features/portal/PortalLoginPage';
import { PortalResultsPage } from '@/features/portal/PortalResultsPage';
import { ConsentBodiesListPage } from '@/features/consent-bodies/ConsentBodiesListPage';
import { ConsentBodyEditPage } from '@/features/consent-bodies/ConsentBodyEditPage';

export function AppRouter(): JSX.Element {
  return (
    <Routes>
      {/* Públicas */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Portal público del paciente — sin AppShell, sin RequireAuth.
          La sesión vive en sessionStorage; verificación contra /api/portal. */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal" element={<PortalResultsPage />} />

      {/* Protegidas */}
      <Route element={<RequireAuth />}>
        {/* Sin AppShell — vistas imprimibles sin sidebar */}
        <Route path="/pacientes/:id/imprimir" element={<PatientPrintPage />} />
        <Route path="/procedimientos/:id/imprimir" element={<ProcedurePrintPage />} />

        <Route element={<AppShell />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/pacientes" element={<PatientsListPage />} />
          <Route path="/pacientes/nuevo" element={<PatientNewPage />} />
          <Route path="/pacientes/:id" element={<PatientDetailPage />} />
          <Route path="/pacientes/:id/editar" element={<PatientEditPage />} />
          <Route path="/procedimientos" element={<ProceduresListPage />} />
          <Route path="/procedimientos/nuevo" element={<ProcedureNewPage />} />
          <Route path="/procedimientos/:id/editar" element={<ProcedureEditPage />} />
          <Route path="/procedimientos/:id" element={<ProcedureDetailPage />} />
          <Route
            path="/consentimiento"
            element={<ClinicalDocsListPage kind="consent" />}
          />
          <Route
            path="/consentimiento/:procedimientoId"
            element={<ConsentSignPage />}
          />
          <Route
            path="/anestesia"
            element={<ClinicalDocsListPage kind="anesthesia" />}
          />
          <Route
            path="/anestesia/:procedimientoId"
            element={<AnesthesiaSignPage />}
          />
          <Route path="/gasto" element={<ClinicalDocsListPage kind="expense" />} />
          <Route path="/gasto/:procedimientoId" element={<ExpenseSignPage />} />
          <Route path="/patologia" element={<PathologyListPage />} />
          <Route path="/patologia/nueva" element={<PathologyNewPage />} />
          <Route path="/historial" element={<ConsultaHistoricaPage />} />
          <Route path="/reportes" element={<ReportsPage />} />
          <Route path="/payroll" element={<PayrollPage />} />

          {/* Sección SISTEMA — solo el súper administrador (rol admin) */}
          <Route element={<RequireRole allow={['admin']} />}>
            <Route path="/usuarios" element={<UsersListPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/admin/consentimientos" element={<ConsentBodiesListPage />} />
            <Route
              path="/admin/consentimientos/:cups"
              element={<ConsentBodyEditPage />}
            />
            <Route path="/catalogo" element={<CatalogPage />} />
            <Route path="/auditoria" element={<AuditLogPage />} />
            <Route path="/configuracion" element={<ConfiguracionPage />} />
          </Route>
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function NotFoundPage(): JSX.Element {
  return (
    <div className="min-h-screen grid place-items-center p-8 bg-bg">
      <div className="bg-surface border border-border rounded-lg shadow-sm p-8 max-w-md w-full text-center">
        <div className="inline-grid place-items-center w-12 h-12 rounded-md bg-primary text-white font-extrabold text-lg mb-4">
          Em
        </div>
        <h1 className="text-xl font-bold mb-2">404 — Página no encontrada</h1>
        <p className="text-text-muted text-sm">
          La ruta solicitada no existe. Vuelve al{' '}
          <a href="/dashboard" className="text-primary font-semibold">
            dashboard
          </a>
          .
        </p>
      </div>
    </div>
  );
}
